"use client";
import "./Footer.css";
import { useEffect, useRef, useState } from "react";
import { Layout, Row, Col, Typography, Space, Image } from "antd";
import { useIntl } from "react-intl";
import { usePathname } from "next/navigation";
import { useRecState } from "../../Orchestrator/Recoils/Events/Recoil";
import { getCmsNetworkMapStationsWithoutBestPriceAction } from "../../Actions/LogicActions/CmsNetworkMapStations/GetCmsNetworkMapStationsWithoutBestPriceAction";
import { getFooterTopRoutesAction, type FooterTopRoute } from "../../Actions/LogicActions/Search/GetFooterTopRoutesAction";
import { stationMoreDetailClickAction } from "../../Actions/UIActions/CmsNetworkMapStations/StationMoreDetailClickAction";
import { fireRouteSearchByStationId } from "./footerRouteSearch";
import FooterLinkColumn from "./FooterLinkColumn";
import {
  ABOUT_LINKS,
  BOOK_JOURNEY_LINKS,
  SUPPORT_LINKS,
  SOCIAL_LINKS,
} from "./footerConfig";
import type { ICmsNetworkMapStations } from "../../Orchestrator/Types/Interface/ICmsNetworkMapStations";
import packageJson from "../../../package.json";

const { Footer: AntFooter } = Layout;
const { Text, Title } = Typography;

// Strip the word "Station" (EN) / "محطة" (AR) from a station name so the footer
// Top Routes read "Abu Dhabi to Dubai" instead of "Abu Dhabi Station to Dubai Station".
const stripStationWord = (name: string): string => {
  const cleaned = (name || "")
    .replace(/\bstations?\b/gi, "") // EN "Station"/"Stations"
    .replace(/(^|\s)(?:ال)?محطة(?=\s|$)/g, "$1") // AR "محطة"/"المحطة" as a standalone word
    .replace(/\s+/g, " ")
    .trim();
  return cleaned || (name || "").trim();
};

const HIDDEN_PATHS = [
  "/checkout",
  "/bookings/change-date",
  "/book-return-journey",
  "/cancel",
  "/change-date/outbound",
  "/upgrade-premium",
  "/multi-booking",
];

const Footer = () => {
  const intl = useIntl();
  const dir = intl.locale === "ar" ? "rtl" : "ltr";
  const pathname = usePathname();
  const [stations] = useRecState<ICmsNetworkMapStations[]>("networkMapStationResponseAtom");
  const stationsFetchAttempted = useRef(false);
  const [topRoutes, setTopRoutes] = useState<FooterTopRoute[]>([]);

  useEffect(() => {
    // Fetch at most once: a CMS failure leaves the atom empty, so keying the
    // re-run on the array would retry in a tight loop.
    if (!stationsFetchAttempted.current && (!stations || stations.length === 0)) {
      stationsFetchAttempted.current = true;
      getCmsNetworkMapStationsWithoutBestPriceAction.execute();
    }
  }, [stations]);

  useEffect(() => {
    // Top Routes are CMS-driven (popular trips). The action dedupes concurrent
    // calls, and the cancelled flag guards against a post-unmount state update
    // (e.g. React StrictMode's mount/cleanup/remount in dev).
    let cancelled = false;
    (async () => {
      await getFooterTopRoutesAction.execute();
      if (!cancelled) setTopRoutes(getFooterTopRoutesAction.result || []);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleStationClick = (slug: string) => {
    if (!slug) return;
    stationMoreDetailClickAction.setParams({ slug });
    stationMoreDetailClickAction.execute();
  };

  if (!pathname || HIDDEN_PATHS.some((p) => pathname.includes(p))) {
    return null;
  }

  return (
    <AntFooter className={`footer ${dir}`} dir={dir}>
      <div className="footer-top">
        <div className="container">
          <div className="footer-in">
            <Row gutter={[24, 24]} align="top" className="footer-content">
              {/* Top Routes */}
              <Col xs={24} sm={12} md={8} lg={5}>
                <Title level={4} className="footer-title">
                  {intl.formatMessage({ id: "topRoutesTitle" })}
                </Title>
                <ul className="footer-list">
                  {topRoutes.map((route, i) => {
                    // Use the CMS (locale-specific) names, stripping the
                    // "Station"/"محطة" word for the compact Top Routes label.
                    const from = stripStationWord(route.originName);
                    const to = stripStationWord(route.destinationName);
                    return (
                      <li key={`${route.originCode}-${route.destinationCode}-${i}`}>
                        <button
                          type="button"
                          className="footer-link footer-link-button"
                          onClick={() =>
                            fireRouteSearchByStationId(route.originCode, route.destinationCode, intl)
                          }
                        >
                          {intl.formatMessage(
                            { id: "footerRouteFromTo" },
                            { from, to },
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </Col>

              {/* Stations (network-map stations) */}
              <Col xs={24} sm={12} md={8} lg={5}>
                <Title level={4} className="footer-title">
                  {intl.formatMessage({ id: "stationsTitle" })}
                </Title>
                <ul className="footer-list">
                  {(stations || []).map((s) => (
                    <li key={s.slug}>
                      <button
                        type="button"
                        className="footer-link footer-link-button"
                        onClick={() => handleStationClick(s.slug)}
                      >
                        {s.station?.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </Col>

              {/* About Etihad Rail */}
              <Col xs={24} sm={12} md={8} lg={5}>
                <FooterLinkColumn titleKey="aboutEtihadRailTitle" links={ABOUT_LINKS} />
              </Col>

              {/* Book Your Journey */}
              <Col xs={24} sm={12} md={8} lg={5}>
                <FooterLinkColumn titleKey="bookYourJourneyTitle" links={BOOK_JOURNEY_LINKS} />
              </Col>

              {/* Support */}
              <Col xs={24} sm={12} md={8} lg={4}>
                <FooterLinkColumn titleKey="supportTitle" links={SUPPORT_LINKS} />
              </Col>
            </Row>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container">
          <div className="footer-in">
            <Row gutter={[24, 24]} align="middle" justify="space-between" className="footer-bottom-row">
              <Col xs={24} md={8}>
                <Image
                  src="/images/black_logo.svg"
                  alt="Etihad Rail"
                  preview={false}
                  width={150}
                  height={"100%"}
                  className="footer-logo responsive-img"
                />
              </Col>
              <Col xs={24} md={12} className="footer-social">
                <div className="footer-social-box">
                  <Space size="small" className="footer-social-icons">
                    {SOCIAL_LINKS.map((s) => {
                      const icon = (
                        <Image src={s.icon} width={24} height={24} alt={s.id} preview={false} />
                      );
                      return s.url ? (
                        <a
                          key={s.id}
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="footer-social-link"
                          aria-label={s.id}
                        >
                          {icon}
                        </a>
                      ) : (
                        <span key={s.id} className="footer-social-link">
                          {icon}
                        </span>
                      );
                    })}
                  </Space>
                  <span className="footer-social-caption">
                    {intl.formatMessage({ id: "followOnSocial" })}
                  </span>
                </div>
              </Col>
            </Row>

            <Row justify="center" className="footer-copyright">
              <Text className="footer-bottom-text">
                &copy; {new Date().getFullYear()} {intl.formatMessage({ id: "companyName" })}.{" "}
                {intl.formatMessage({ id: "allRightsReserved" })}
              </Text>
            </Row>

            <Row justify="center" className="footer-version">
              <Text className="footer-version-text">{intl.formatMessage({ id: "version" })}: {packageJson.version}</Text>
            </Row>
          </div>
        </div>
      </div>
    </AntFooter>
  );
};

export default Footer;
